import { angularjs } from '@app/service/migration-helpers/angularjs.injection';
import { inject, Injectable, Injector } from '@angular/core';
import type { Observable } from 'rxjs';
import { catchError } from 'rxjs';
import { map } from 'rxjs/operators';
import type { HttpErrorResponse } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import type { MasterEventDto } from '@app/interface/response/master-event.dto';
import type { SearchAccountDto } from '@app/interface/response/search-account.dto';
import type { IAnalyticsService } from '@app/interface/analytic/AnalyticsService.interface';
import App from '@app/app';
import { downgradeInjectable } from '@angular/upgrade/static';

export interface GetAccountResponse {
  success: boolean;
  data: MasterEventsResponse;
  code: string | null;
}

export interface MasterEventsResponse {
  masterEvents: MasterEventDto[];
}

export interface GetAccountsResponse {
  success: boolean;
  data: { accounts: SearchAccountDto[] };
  error: { code: string } | null;
}

const FIND_MY_PICTURE_DAY_GET_ACCOUNTS_PATH = 'account/findMyPictureDay/getAccounts';
const GET_ACCOUNTS_PATH = 'account/getAccounts';

@Injectable({
  providedIn: 'root'
})
export class AccountApi {
  private analyticsService: IAnalyticsService = inject<IAnalyticsService>(angularjs.AnalyticsService);
  private injector = inject(Injector);

  private get http(): HttpClient {
    return this.injector.get(HttpClient);
  }

  getAccount(lifetouchId: string): Observable<MasterEventsResponse> {
    return this.http.get<GetAccountResponse>(`account/getAccount/${lifetouchId}`).pipe(
      catchError((error: HttpErrorResponse) => {
        throw new Error(error?.error?.error?.code ?? 'UNKNOWN_ERROR');
      }),
      map((response) => {
        if (response?.success) {
          return response.data;
        }
        this.analyticsService.addAdobeFormErrorEvent(
          this.analyticsService.ADOBE_FORM_IDS.GUEST_FIND_MY_PICTURE_DAY,
          this.analyticsService.ADOBE_ERROR_DESCRIPTIONS.SEARCH_ERROR
        );
        throw new Error(response?.code ?? 'UNKNOWN_ERROR');
      })
    );
  }

  getAccounts(
    stateProvince: string | null,
    accountName: string,
    zipCode: string,
    isFindMyPictureDay: boolean
  ): Observable<SearchAccountDto[]> {
    const uriPath = isFindMyPictureDay ? FIND_MY_PICTURE_DAY_GET_ACCOUNTS_PATH : GET_ACCOUNTS_PATH;
    const params: Record<string, string> = { accountName, zipCode };
    if (stateProvince) {
      params.stateProvince = stateProvince;
    }
    return this.http.get<GetAccountsResponse>(uriPath, { params }).pipe(
      catchError((error: HttpErrorResponse) => {
        throw new Error(error?.error?.error?.code ?? 'UNKNOWN_ERROR');
      }),
      map((response) => {
        if (response?.success) {
          return response.data.accounts;
        }
        this.analyticsService.addAdobeFormErrorEvent(
          this.analyticsService.ADOBE_FORM_IDS.GUEST_FIND_MY_PICTURE_DAY,
          this.analyticsService.ADOBE_ERROR_DESCRIPTIONS.SEARCH_ERROR
        );
        throw new Error(response?.error?.code ?? 'UNKNOWN_ERROR');
      })
    );
  }
}
App.factory('AccountApi', downgradeInjectable(AccountApi));
